import { InputAdornment } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';

import { BaseInputProps, StyledTextField } from './StyledTextField';

export interface DecimalInputProps extends BaseInputProps {
  /** Valor numérico decimal o null */
  value: number | null;
  /** Callback cuando cambia el valor */
  onChange: (value: number | null) => void;
  /** Valor mínimo permitido */
  min?: number;
  /** Valor máximo permitido */
  max?: number;
  /** Número de decimales permitidos (default: 2) */
  decimalPlaces?: number;
  /** Permitir números negativos (default: false) */
  allowNegative?: boolean;
  /** Si es true, muestra % y valida 0-100 (default: false) */
  isPercentage?: boolean;
  /** Nombre del campo para formularios */
  name?: string;
}

/**
 * Características:
 * - Procesa input como string para formateo correcto
 * - Permite escritura decimal con validación en tiempo real
 * - Soporta porcentaje (0-100) con validación
 * - Devuelve number | null en onChange
 */
export const DecimalInput: React.FC<DecimalInputProps> = ({
  value,
  onChange,
  label,
  disabled = false,
  error = false,
  helperText,
  required = false,
  fullWidth = true,
  placeholder,
  min,
  max,
  decimalPlaces = 2,
  allowNegative = false,
  isPercentage = false,
  name,
}) => {
  const [displayValue, setDisplayValue] = useState<string>(
    value !== null ? String(value) : '',
  );

  // Determinar min/max basado en isPercentage
  const effectiveMin = isPercentage ? 0 : min;
  const effectiveMax = isPercentage ? 100 : max;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let rawValue = e.target.value;

      // Reemplazar coma por punto para soporte internacional
      rawValue = rawValue.replace(',', '.');

      // Permitir string vacío
      if (rawValue === '') {
        setDisplayValue('');
        onChange(null);
        return;
      }

      // Permitir solo punto decimal
      if (rawValue === '.') {
        setDisplayValue('0.');
        return;
      }

      // Permitir solo signo negativo al inicio si allowNegative
      if (allowNegative && rawValue === '-') {
        setDisplayValue('-');
        return;
      }

      // Construir regex para validación
      const negativePrefix = allowNegative ? '-?' : '';
      const decimalPart =
        decimalPlaces > 0 ? `(\\.\\d{0,${decimalPlaces}})?` : '';
      const regex = new RegExp(`^${negativePrefix}\\d*${decimalPart}$`);

      if (!regex.test(rawValue)) {
        return;
      }

      const numValue = parseFloat(rawValue);
      const endsWithDot = rawValue.endsWith('.');

      // Validar rango (solo si es un número válido, no termina en punto, y no es un valor temporal)
      // Permitir escribir valores temporales como "0." para poder continuar escribiendo
      if (!isNaN(numValue) && !endsWithDot) {
        if (effectiveMin !== undefined && numValue < effectiveMin) {
          return;
        }
        if (effectiveMax !== undefined && numValue > effectiveMax) {
          return;
        }
      }

      // Siempre actualizar el display value para permitir escritura continua
      setDisplayValue(rawValue);

      // Solo emitir onChange si es un número válido y completo (no termina en punto)
      if (!isNaN(numValue) && !endsWithDot) {
        onChange(numValue);
      } else if (endsWithDot) {
        // Si termina en punto, no emitir onChange para permitir continuar escribiendo
        // pero mantener el valor en display
      }
    },
    [onChange, allowNegative, decimalPlaces, effectiveMin, effectiveMax],
  );

  const handleBlur = useCallback(() => {
    // Limpiar valores incompletos al perder foco
    if (displayValue === '.' || displayValue === '-' || displayValue === '-.') {
      setDisplayValue('');
      onChange(null);
      return;
    }

    // Formatear el número al perder foco
    if (displayValue !== '' && !isNaN(parseFloat(displayValue))) {
      const numValue = parseFloat(displayValue);
      setDisplayValue(String(numValue));
    }
  }, [displayValue, onChange]);

  // Sincronizar con valor externo
  useEffect(() => {
    const newDisplayValue = value !== null ? String(value) : '';
    // Solo actualizar si el valor numérico cambió y no estamos en medio de escribir un decimal
    // (no actualizar si displayValue termina en punto, ya que el usuario está escribiendo)
    if (value !== null) {
      const currentNum = parseFloat(displayValue);
      const isWritingDecimal = displayValue.endsWith('.');

      // Solo actualizar si el valor realmente cambió y no estamos escribiendo un decimal
      if (!isWritingDecimal && (isNaN(currentNum) || currentNum !== value)) {
        setDisplayValue(newDisplayValue);
      }
    } else if (
      displayValue !== '' &&
      displayValue !== '-' &&
      !displayValue.endsWith('.')
    ) {
      setDisplayValue('');
    }
  }, [value, displayValue]);

  return (
    <StyledTextField
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      label={label}
      disabled={disabled}
      error={error}
      helperText={helperText}
      required={required}
      fullWidth={fullWidth}
      placeholder={placeholder}
      name={name}
      InputProps={
        isPercentage
          ? {
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }
          : undefined
      }
    />
  );
};
